import { apiFetch } from '../utils/httpClient';

export interface AuthUser {
  id: string;
  email: string;
  username?: string | null;
  email_verified: boolean;
  role?: 'system_admin' | 'system_user' | 'user' | 'admin';  // Support new and legacy roles
  organization_id?: string | null;
  organization_role?: 'owner' | 'admin' | 'coach' | 'scout' | 'analyst' | 'member' | null;
  subscription_status?: string;
  subscription_type?: string;
  created_at: string;
  updated_at: string;
  last_login_at?: string | null;
}

interface AuthResponse {
  user: AuthUser;
}

const parseResponse = async <T>(response: Response): Promise<T> => {
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    // Standardized error handling
    let message = 'Authentication request failed';
    let statusCode = response.status;
    
    if (data) {
      // Handle validation errors (422) - detail is an array
      if (Array.isArray(data.detail)) {
        const emailError = data.detail.find((err: any) => 
          err.loc && Array.isArray(err.loc) && err.loc.includes('email')
        );
        if (emailError) {
          message = 'Email is invalid. Please enter a valid email address.';
        } else {
          message = data.detail[0]?.msg || 'Validation error';
        }
      } 
      // Handle simple string detail
      else if (typeof data.detail === 'string') {
        message = data.detail;
      }
      // Handle object with message
      else if (data.message) {
        message = data.message;
      }
    }
    
    // Create enhanced error with status code
    const error = new Error(message) as Error & { statusCode?: number };
    error.statusCode = statusCode;
    throw error;
  }
  return data as T;
};

export const authService = {
  async register(email: string, password: string): Promise<AuthUser> {
    const response = await apiFetch('/api/v1/auth/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    return parseResponse<AuthUser>(response);
  },

  async login(email: string, password: string): Promise<AuthUser> {
    const response = await apiFetch('/api/v1/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    return parseResponse<AuthUser>(response);
  },

  async logout(): Promise<void> {
    const response = await apiFetch('/api/v1/auth/logout', { method: 'POST' });
    if (!response.ok) {
      throw new Error('Failed to logout');
    }
  },

  async getCurrentUser(): Promise<AuthUser> {
    const response = await apiFetch('/api/v1/auth/me');
    return parseResponse<AuthUser>(response);
  },

  async refreshSession(): Promise<AuthUser> {
    const response = await apiFetch('/api/v1/auth/refresh', { method: 'POST' });
    const data = await parseResponse<AuthResponse>(response);
    return data.user;
  },
};




